import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Overline, Reveal } from "./Reveal";

const CHAPTERS = [
    {
        number: "01",
        title: "Serene Ambience",
        text: "Soft light, hushed tones and the gentle warmth of candle glow — a calming atmosphere that begins to work the moment you arrive.",
        image: "/images/experience.jpg",
        alt: "Cream towels, orchid and candlelight at Flammingo Luxurious Spa, Vashi",
    },
    {
        number: "02",
        title: "Attentive Service",
        text: "A welcoming, unhurried approach throughout your visit — every detail considered, every preference quietly remembered.",
        image: "/images/aroma.jpg",
        alt: "Aromatherapy ritual prepared with care at Flammingo Luxurious Spa",
    },
    {
        number: "03",
        title: "Private Moments",
        text: "Luxury interiors and secluded spaces reserved for you alone — private relaxation, away from everything.",
        image: "/images/jacuzzi.jpg",
        alt: "Private jacuzzi suite at Flammingo Luxurious Spa, Navi Mumbai",
    },
];

const Chapter = ({ chapter, index }) => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);
    const flip = index % 2 === 1;

    return (
        <div
            ref={ref}
            className="grid items-center gap-12 lg:grid-cols-12 lg:gap-16"
        >
            <Reveal
                className={`lg:col-span-6 ${flip ? "lg:order-2" : ""}`}
            >
                <div className="relative aspect-[16/11] overflow-hidden">
                    <motion.img
                        style={{ y }}
                        src={chapter.image}
                        alt={chapter.alt}
                        loading="lazy"
                        className="h-full w-full scale-110 object-cover"
                    />
                    <div className="absolute inset-0 bg-emerald-950/10" />
                </div>
            </Reveal>
            <div className={`lg:col-span-6 ${flip ? "lg:order-1" : ""}`}>
                <Reveal>
                    <span className="font-serif text-6xl font-light italic leading-none text-gold/25 md:text-7xl">
                        {chapter.number}
                    </span>
                </Reveal>
                <Reveal delay={0.1}>
                    <h3 className="mt-4 font-serif text-3xl tracking-wide text-cream md:text-4xl">
                        {chapter.title}
                    </h3>
                </Reveal>
                <Reveal delay={0.2}>
                    <p className="mt-6 max-w-md text-base font-light leading-relaxed tracking-wide text-sage">
                        {chapter.text}
                    </p>
                </Reveal>
                <Reveal delay={0.25}>
                    <span className="mt-8 block h-px w-24 bg-gold/40" aria-hidden="true" />
                </Reveal>
            </div>
        </div>
    );
};

const Experience = () => (
    <section
        id="experience"
        data-testid="experience-section"
        className="relative bg-emerald-900/25 py-28 md:py-36"
    >
        <div className="mx-auto max-w-7xl px-6 md:px-12">
            <div className="mb-20 max-w-3xl md:mb-28">
                <Reveal>
                    <Overline>The Flammingo Experience</Overline>
                </Reveal>
                <Reveal delay={0.1}>
                    <h2 className="mt-6 font-serif text-4xl leading-tight tracking-tight text-cream sm:text-5xl md:text-6xl">
                        Your Time.{" "}
                        <span className="italic text-gold-light">
                            Your Escape.
                        </span>
                    </h2>
                </Reveal>
            </div>
            <div className="flex flex-col gap-24 md:gap-32">
                {CHAPTERS.map((chapter, i) => (
                    <Chapter key={chapter.number} chapter={chapter} index={i} />
                ))}
            </div>
        </div>
    </section>
);

export default Experience;
